import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FocusScope } from "react-aria";
import { Icon } from "@iconify/react/dist/iconify.js";

import { useGeneralHeaderDropdown } from "../../../../../hooks/header/useGeneralHeaderDropdown";
import { useId } from "../../../../../hooks/use-id";
import { usePersonalAccountPermissions } from "../../../../../hooks/use-personal-account-permissions";
import { useNetworkStatus } from "../../../../../hooks/use-network-status";
import { useLogout } from "../../../../../hooks/useLogout";

import { ModoPWAContext } from "../../../../../contexts/ModoPwaContext";
import { ASSETS_URL, store } from "../../../../Store";

import { HeaderQuickActions } from "../../../components/HeaderQuickActions";
import { MenuItem } from "../../../components/MenuItem";
import { IconAssociateProfile } from "./IconAssociateProfile";
import { SubMenu } from "../../../components/SubMenu";

/**
 * Menú desplegable responsive del header para usuarios autenticados.
 *
 * Este componente renderiza el menú principal de navegación en dispositivos
 * móviles o pantallas reducidas. Su contenido cambia dinámicamente según:
 *
 * - El tipo de cuenta (personal o institucional).
 * - Los permisos del usuario.
 * - El estado de la aplicación (PWA o navegador).
 * - La disponibilidad de conexión.
 *
 * Incluye accesos a:
 * - Perfil.
 * - Institución.
 * - Crear o asociar perfil.
 * - Descargas.
 * - Favoritos.
 * - Comunidad.
 * - Recursos.
 * - Mi panel.
 * - Acciones rápidas.
 * - Cerrar sesión.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.textosInterfaz - Objeto con los textos traducidos utilizados en la interfaz.
 *
 * @returns {JSX.Element} HGDropdownResponsive
 */
export const HGDropdownResponsive = ({ textosInterfaz }) => {
    const showADropdown = useGeneralHeaderDropdown();
    const visible = showADropdown?.state?.visible;

    const ref = useRef(null);

    const menuId = useId("user-menu-responsive");

    const pwaState = useContext(ModoPWAContext);
    const globalState = useContext(store);
    const { state } = globalState;

    const isPersonalAccount = state?.isPersonalAccount;
    const personalAccountName = state?.personalAccount?.fullname || 'User';

    const personalAccountPermissions = usePersonalAccountPermissions();

    const canViewProfile = personalAccountPermissions.hasPermission('view');
    const canEditProfile = personalAccountPermissions.hasPermission('edit');
    const canCreateProfile = personalAccountPermissions.hasPermission('create');
    const isAlternativeFlow = personalAccountPermissions.hasPermission('alternative-association-flow');

    const networkStatus = useNetworkStatus();
    const logoutHandler = useLogout();

    const [resourcesOptions, setResourcesOptions] = useState(false);

    useEffect(() => {
        if (!visible) {
            setResourcesOptions(false);
        }
    }, [visible]);

    const closeMenu = () => {
        showADropdown.setVisible(false);
        setResourcesOptions(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            closeMenu();
        }
    };

    const resourceItems = [
        {
            id: "resources",
            link: "/comunicacion",
            name: textosInterfaz?.comunicacion ?? "Comunicación",
        },
        // {
        //     id: "pedagogical",
        //     link: "/centro-pedagogico",
        //     name: textosInterfaz?.centro_pedagogico ?? "Centro pedagógico",
        // },
        {
            id: "blog",
            link: "/blog",
            name: textosInterfaz?.blog ?? "Blog",
        },
        {
            id: "help",
            link: "/ayuda",
            name: textosInterfaz?.ayuda ?? "Ayuda",
        },
        {
            id: "apps",
            link: "https://makemake.app/home?platform=macos",
            name: "Apps",
            external: true,
        },
    ];

    const MENU_ITEM_BASE_CLASS =
        "link-principal d-flex justify-content-center align-items-center focusable-red";

    const showProfile =
        isPersonalAccount &&
        canViewProfile &&
        canEditProfile;

    const showInstitution =
        isPersonalAccount &&
        canViewProfile;

    const showCreateProfile =
        !isPersonalAccount &&
        canCreateProfile;

    const showAssociateProfile =
        !isPersonalAccount &&
        isAlternativeFlow;

    const showDownloads =
        pwaState?.clienteHabilitado &&
        pwaState?.compatible;

    const showPanel =
        state.userType === "Cliente" &&
        !isPersonalAccount;

    const showLogout =
        !pwaState.appInstalada &&
        networkStatus.online === true;

    return (
        <FocusScope contain restoreFocus autoFocus>
            <div
                className={`dropdownMenuResponsive ${visible ? 'activeMode' : 'inactiveMode'}`}
                onKeyDown={handleKeyDown}
            >
                <ul
                    ref={ref}
                    role="menu"
                    id={menuId}
                    aria-label="Menú de usuario"
                >
                    {/* Perfil
                    {showProfile && (
                        <>
                            <li
                                className={`${visible
                                    ? 'activeMode'
                                    : 'inactiveMode'
                                    } d-flex align-items-center justify-content-center`}
                                role="none"
                            >
                                <Link
                                    to="/perfil"
                                    onClick={closeMenu}
                                    className={`${MENU_ITEM_BASE_CLASS}`}
                                    role="menuitem"
                                >
                                    <Icon
                                        icon="fluent:person-32-filled"
                                        width={20}
                                        className="mr-2"
                                    />
                                    <span
                                        className="menu-user-name"
                                        title={personalAccountName}
                                    >
                                        {personalAccountName}
                                    </span>
                                </Link>
                            </li>
                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>

                    )}

  
                    {showInstitution && (
                        <>
                            <MenuItem
                                visible={visible}
                                to="/perfil/institucion"
                                icon="tdesign:institution-filled"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                <span
                                    className="menu-user-name"
                                    title={textosInterfaz?.institucion || 'Instituciones'}
                                >
                                    {textosInterfaz?.institucion || 'Instituciones'}
                                </span>
                            </MenuItem>
                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>
                    )}

  
                    {showCreateProfile &&
                        <>
                            <MenuItem
                                visible={visible}
                                to="/crear-perfil"
                                icon="mdi:register"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                {textosInterfaz?.crear_perfil ||
                                    "Crear perfil"}
                            </MenuItem>

                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>
                    }

   
                    {showAssociateProfile &&
                        <>
                            <MenuItem
                                visible={visible}
                                to="/asociar-institucion-perfil"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                <IconAssociateProfile />
                                {textosInterfaz
                                    ?.asociar_perfil_flujo_alternativo ||
                                    "Asociar perfil"}
                            </MenuItem>

                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>
                    } */}

                    {/* Descargas */}
                    {showDownloads && (
                        <>
                            <MenuItem
                                visible={visible}
                                to="/descargados"
                                icon="tabler:book-download"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                {textosInterfaz?.descargas ||
                                    "Descargas"}
                            </MenuItem>

                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>
                    )}

                    {/* Favoritos */}
                    <MenuItem
                        visible={visible}
                        to="/favoritos"
                        icon="tabler:heart"
                        onClick={closeMenu}
                        className={`${MENU_ITEM_BASE_CLASS}`}
                    >
                        {textosInterfaz?.favoritos || "Favoritos"}
                    </MenuItem>

                    <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Comunidad */}
                    <MenuItem
                        visible={visible}
                        to="/comunidad"
                        onClick={closeMenu}
                        className={`${MENU_ITEM_BASE_CLASS}`}
                    >
                        {textosInterfaz?.comunidad || "Comunidad"}
                    </MenuItem>

                    <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Recursos */}
                    <SubMenu
                        visible={visible}
                        open={resourcesOptions}
                        onToggle={() => {
                            setResourcesOptions((prev) => !prev)
                        }}
                        icon="tabler:cards"
                        label={
                            textosInterfaz?.recursos2 ||
                            "Recursos"
                        }
                        items={resourceItems}
                        onItemClick={closeMenu}
                        idPrefix="recursos"
                    />

                    <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>

                    {/* Panel */}
                    {showPanel && (
                        <>
                            <MenuItem
                                visible={visible}
                                to="/mi-panel/mi-cuenta"
                                onClick={closeMenu}
                                className={`${MENU_ITEM_BASE_CLASS}`}
                            >
                                <div
                                    className="btn-avatar mr-3"
                                    style={{
                                        backgroundImage: `url('${state.datosHeader?.avatar
                                            ? `${ASSETS_URL}${state.datosHeader.avatar}`
                                            : "/img/profile.png"
                                            }')`,
                                    }}
                                />

                                {textosInterfaz?.panel || "Mi panel"}
                            </MenuItem>

                            <div role="separator" className={`divider ${visible ? 'activeMode' : 'inactiveMode'}`} ></div>
                        </>
                    )}

                    {/* Acciones rápidas */}
                    <li
                        className={`${visible
                            ? 'activeMode'
                            : 'inactiveMode'
                            }`}
                    >
                        <HeaderQuickActions
                            textosInterfaz={textosInterfaz}
                            cerrarVentana={closeMenu}
                        />
                    </li>

                    {showLogout && (
                        <li
                            className={`${visible
                                ? "activeMode mb-4"
                                : "inactiveMode"
                                }`}
                            role="none"
                        >
                            <button
                                type="button"
                                className="link-principal log-out-button w-100 d-flex justify-content-center align-items-center focusable-red"
                                onClick={(event) => {
                                    event.preventDefault();

                                    logoutHandler.logout();
                                    closeMenu();
                                }}
                            >
                                <span
                                    role="status"
                                    aria-live="polite"
                                >
                                    {logoutHandler?.loading
                                        ? textosInterfaz?.cerrando ||
                                        "Cerrando sesión..."
                                        : textosInterfaz?.cerrar_sesion ||
                                        "Cerrar sesión"}
                                </span>
                            </button>
                        </li>
                    )}
                </ul>
            </div>
        </FocusScope >
    )
}